/**
 * ExportCostDialog — the styled pre-conversion confirmation that replaces the
 * old window.confirm in the Supplement export flow. Shows how many credits the
 * export will use, an optional "Learn more" expander explaining the pricing
 * tiers, and a "Don't show this again" checkbox that flips the persisted
 * `exportCostConfirmEnabled` flag so future exports skip straight to download.
 */
import { useState } from 'react';

import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { XACTIMATE_PRICE_LIST_NOTICE } from '../lib/notices';

/** Props for {@link ExportCostDialog}. */
export interface ExportCostDialogProps {
  /** Credits this export will use (the parse's `credits_required`). */
  cost: number;
  /** Current credit balance, if known (shown for context). */
  balance: number | null;
  /** Confirm — proceed with the export. `dontAskAgain` reflects the checkbox. */
  onConfirm: (_dontAskAgain: boolean) => void;
  /** Cancel — abort the export. */
  onCancel: () => void;
}

/** Styled "this export will use N credits" confirmation. */
export default function ExportCostDialog({
  cost,
  balance,
  onConfirm,
  onCancel,
}: ExportCostDialogProps) {
  const [learnMore, setLearnMore] = useState(false);
  const [dontAsk, setDontAsk] = useState(false);

  const noun = cost === 1 ? 'credit' : 'credits';

  return (
    <Modal
      label="Confirm export cost"
      title="Export to Xactimate"
      subtitle="Exporting converts this estimate to an importable .ESX file."
      onClose={onCancel}
      testId="export-cost-dialog"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="export-cost-cancel">
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onConfirm(dontAsk)}
            data-testid="export-cost-confirm"
          >
            Export — {cost} {noun}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-[13px] text-[var(--tx-hi)]">
          This conversion will use{' '}
          <span className="font-semibold text-[var(--accent)]">
            {cost} {noun}
          </span>
          {balance !== null && (
            <span className="text-[var(--tx-lo)]">
              {' '}
              — you have {balance}.
            </span>
          )}
        </p>

        <p className="text-[12px] text-[var(--tx-mid)]" data-testid="export-price-list-notice">
          {XACTIMATE_PRICE_LIST_NOTICE}
        </p>

        <button
          type="button"
          onClick={() => setLearnMore((v) => !v)}
          className="text-[12px] text-[var(--tx-lo)] underline underline-offset-2 hover:text-[var(--tx-hi)]"
          data-testid="export-cost-learnmore-toggle"
        >
          {learnMore ? 'Hide details' : 'Learn more'}
        </button>

        {learnMore && (
          <div
            className="rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-inset)] p-3 text-[12px] text-[var(--tx-mid)] space-y-1.5"
            data-testid="export-cost-learnmore"
          >
            <p>Cost depends on the size of the estimate:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>A small estimate uses 1 credit.</li>
              <li>A larger estimate uses more credits.</li>
              <li>Adding a floor-plan sketch doubles the cost.</li>
            </ul>
            <p className="text-[var(--tx-lo)]">Your first 3 conversions are free.</p>
          </div>
        )}

        <label className="flex items-center gap-2 text-[12px] text-[var(--tx-mid)] select-none cursor-pointer">
          <input
            type="checkbox"
            checked={dontAsk}
            onChange={(e) => setDontAsk(e.target.checked)}
            className="accent-[var(--accent)]"
            data-testid="export-cost-dontask"
          />
          Don&rsquo;t show this again
        </label>
      </div>
    </Modal>
  );
}
